import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';

/**
 * SwimLaneConfirmDialog - Confirmation dialog when assigning an employee to a swim lane
 * Shows how many packages are in the lane and allows user to opt-out of auto-assignment
 * Also warns when packages already have assignments that will be cleared
 */
const SwimLaneConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  employeeName,
  swimLaneName,
  packageCount,
  packagesWithAssignments = 0,
  packages,
  loading,
}) => {
  const [autoAssign, setAutoAssign] = useState(true); // Default to true

  const handleConfirm = () => {
    onConfirm(autoAssign);
  };

  const handleClose = () => {
    setAutoAssign(true); // Reset to default on close
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <WarningIcon color="warning" />
          Confirm Swim Lane Assignment
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <DialogContentText>
              You are about to assign <strong>{employeeName}</strong> to the{' '}
              <strong>{swimLaneName}</strong> swim lane.
            </DialogContentText>

            <DialogContentText sx={{ mt: 2 }}>
              There {packageCount === 1 ? 'is' : 'are'} currently{' '}
              <strong>
                {packageCount} package{packageCount !== 1 ? 's' : ''}
              </strong>{' '}
              in this swim lane.
            </DialogContentText>

            {/* Warning: Packages with existing assignments */}
            {packagesWithAssignments > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <strong>{packagesWithAssignments}</strong> package
                {packagesWithAssignments !== 1 ? 's are' : ' is'} already
                assigned to other owners. If you enable auto-assign, these
                existing assignments will be cleared.
              </Alert>
            )}

            {packages && packages.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Packages in this swim lane:
                </Typography>
                <List
                  dense
                  sx={{
                    maxHeight: 200,
                    overflow: 'auto',
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                  }}
                >
                  {packages.slice(0, 10).map((pkg) => (
                    <ListItem key={pkg.id}>
                      <ListItemText
                        primary={pkg.name}
                        secondary={pkg.area_name}
                      />
                    </ListItem>
                  ))}
                  {packageCount > 10 && (
                    <ListItem>
                      <ListItemText
                        primary={`... and ${packageCount - 10} more`}
                        sx={{ fontStyle: 'italic', color: 'text.secondary' }}
                      />
                    </ListItem>
                  )}
                </List>
              </Box>
            )}

            {packageCount > 0 && (
              <Box
                sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={autoAssign}
                      onChange={(e) => setAutoAssign(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Automatically assign <strong>{employeeName}</strong> to
                      all {packageCount} package{packageCount !== 1 ? 's' : ''}{' '}
                      currently in this swim lane
                      {packagesWithAssignments > 0 && (
                        <Typography
                          component="span"
                          variant="body2"
                          color="warning.main"
                        >
                          {' '}
                          (will clear {packagesWithAssignments} existing
                          assignment{packagesWithAssignments !== 1 ? 's' : ''})
                        </Typography>
                      )}
                    </Typography>
                  }
                />
              </Box>
            )}

            <DialogContentText sx={{ mt: 2 }}>
              Do you want to continue?
            </DialogContentText>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={loading}
        >
          Confirm Assignment
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SwimLaneConfirmDialog;
